import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useAuth } from '../../auth/AuthProvider';
import { useTenantProfile } from '../../lib/settings';
import { Icon } from '../../components/Icon';
import { PageHead } from '../../components/ui';
import { useToast } from '../../components/Toast';

/**
 * الحماية — كلمةُ سرٍّ واحدةٌ تحرسُ بابين.
 *
 * البابُ الأوّل: حذفُ عملٍ منجَز (نشاطٌ أو مهمّةٌ عُلّمت تمامًا) محوٌ لتاريخٍ
 * وقع. والبابُ الثاني: كشفُ المعلوماتِ الحسّاسة. وهي مستقلّةٌ عن كلمةِ سرِّ
 * الحساب عمدًا — فالجالسُ إلى جهازٍ مفتوحٍ لا يمحو تاريخًا ولا يكشفُ سرًّا.
 */
export function SecuritySettings() {
  const { atLeast } = useAuth();
  const isOwner = atLeast('OWNER');
  const toast = useToast();
  const queryClient = useQueryClient();
  const profile = useTenantProfile();

  const status = useQuery({
    queryKey: ['settings', 'delete-password'],
    queryFn: () => api<{ set: boolean }>('/settings/delete-password'),
  });

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);

  const isSet = status.data?.set ?? false;
  const settings = (profile.data?.settings ?? {}) as Record<string, unknown>;
  const sensitiveGuarded = settings.sensitiveRequiresPassword !== false;

  const save = useMutation({
    mutationFn: () => api('/settings/delete-password', { method: 'PATCH', body: { password } }),
    onSuccess: () => {
      toast('حُفظت كلمة الحماية', 'success');
      setPassword('');
      setConfirm('');
      void queryClient.invalidateQueries({ queryKey: ['settings', 'delete-password'] });
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ.'),
  });

  const setGuard = useMutation({
    mutationFn: (v: boolean) =>
      api('/settings/preferences', { method: 'PATCH', body: { sensitiveRequiresPassword: v } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['settings'] });
      toast('حُفظ الإعداد', 'success');
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ.'),
  });

  const mismatch = confirm.length > 0 && password !== confirm;
  const tooShort = password.length > 0 && password.length < 4;

  /* قوّةُ الكلمة — مؤشّرٌ بصريٌّ لا شرطٌ يمنع. */
  const strength = (() => {
    let s = 0;
    if (password.length >= 4) s++;
    if (password.length >= 8) s++;
    if (/[0-9]/.test(password) && /[a-zA-Z؀-ۿ]/.test(password)) s++;
    if (/[^\w\s]/.test(password)) s++;
    return s; // 0..4
  })();
  const strengthLabel = ['ضعيفة جدًّا', 'ضعيفة', 'متوسّطة', 'قويّة', 'قويّة جدًّا'][strength];

  return (
    <div className="page">
      <PageHead title="الحماية" subtitle="كلمةٌ واحدةٌ تحرسُ الحذفَ وكشفَ المعلومات الحسّاسة" />

      {/* لافتةُ الحالة — درعٌ متدرّجٌ يتلوّن بحسبِ الضبط */}
      <div className={`sec-hero${isSet ? ' is-on' : ''}`}>
        <span className="sec-hero-ic">
          <Icon name={isSet ? 'check' : 'alert'} size={26} />
        </span>
        <div className="sec-hero-txt">
          <h2>{isSet ? 'الحمايةُ مفعّلة' : 'الحمايةُ غيرُ مضبوطة'}</h2>
          <p>
            {isSet
              ? 'تُطلَبُ كلمةُ الحماية قبل حذفِ المنجَز، وقبل كشفِ المعلوماتِ الحسّاسة إن كان الحارسُ مفعّلًا.'
              : 'لم تُضبط كلمةٌ بعد — حذفُ المنجَز معطَّلٌ تمامًا حتى تُضبط.'}
          </p>
        </div>
        <span className={`sec-hero-badge${isSet ? ' is-on' : ''}`}>
          {isSet ? 'مضبوطة' : 'غير مضبوطة'}
        </span>
      </div>

      {/* البابان اللذان تحرسهما */}
      <div className="sec-guards">
        <div className="sec-guard">
          <span className="sec-guard-ic is-danger">
            <Icon name="trash" size={18} />
          </span>
          <div className="sec-guard-txt">
            <strong>حذفُ الأعمالِ المنجَزة</strong>
            <span>
              الأنشطةُ والمهامُّ المعلَّمةُ منجَزةً لا تُحذف إلّا بيدِ المالكِ وبهذه الكلمة.
              المخطَّطُ الذي لم يُنجز بعد يُحذف بتأكيدٍ عاديٍّ بلا كلمة.
            </span>
            <span className="sec-guard-state is-locked">
              <Icon name="check" size={12} /> محروسٌ دائمًا
            </span>
          </div>
        </div>

        <div className="sec-guard">
          <span className="sec-guard-ic is-brand">
            <Icon name="eye" size={18} />
          </span>
          <div className="sec-guard-txt">
            <strong>كشفُ المعلوماتِ الحسّاسة</strong>
            <span>
              تُطلَبُ الكلمةُ قبل إظهارِ ما يُخفى في الشاشات — فلا يكشفُه جالسٌ عابرٌ إلى
              جهازٍ مفتوح.
            </span>
            <label className={`switch sec-toggle${sensitiveGuarded ? ' is-on' : ''}`}>
              <input
                type="checkbox"
                checked={sensitiveGuarded}
                disabled={!isOwner || setGuard.isPending}
                onChange={(e) => setGuard.mutate(e.target.checked)}
              />
              <span className="switch-track">
                <span className="switch-thumb" />
              </span>
              <span className="sec-toggle-label">
                {sensitiveGuarded ? 'الحارسُ مفعّل' : 'الحارسُ مُطفأ'}
              </span>
            </label>
          </div>
        </div>
      </div>

      {/* ضبطُ الكلمة */}
      <div className="card mt-4 sec-card">
        <div className="card-head">
          <h2 className="card-title">
            <Icon name="settings" size={16} /> {isSet ? 'تبديلُ كلمة الحماية' : 'ضبطُ كلمة الحماية'}
          </h2>
          <span className="tiny mute">مستقلّةٌ عن كلمة سرّ حسابك</span>
        </div>
        <div className="card-body">
          {!isOwner ? (
            <div className="alert alert-info" style={{ margin: 0 }}>
              <Icon name="info" size={15} />
              <div>ضبطُ كلمةِ الحماية للمالكِ وحده — وأنت ترى الحالةَ فقط.</div>
            </div>
          ) : (
            <>
              <div className="sec-form">
                <label className="field">
                  <span className="label req">الكلمة الجديدة</span>
                  <div className="sec-pass">
                    <input
                      className="input"
                      type={show ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="أربعة أحرف على الأقل"
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="sec-pass-eye"
                      onClick={() => setShow((v) => !v)}
                      title={show ? 'إخفاء' : 'إظهار'}
                      aria-label={show ? 'إخفاء الكلمة' : 'إظهار الكلمة'}
                    >
                      <Icon name="eye" size={15} />
                    </button>
                  </div>
                  {tooShort && <span className="sec-err">أربعةُ أحرفٍ على الأقل.</span>}
                </label>

                <label className="field">
                  <span className="label req">تأكيدها</span>
                  <input
                    className="input"
                    type={show ? 'text' : 'password'}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    autoComplete="new-password"
                  />
                  {mismatch && <span className="sec-err">الكلمتان لا تتطابقان.</span>}
                </label>
              </div>

              {password.length > 0 && (
                <div className="sec-strength">
                  <div className={`sec-strength-bar s${strength}`}>
                    <span />
                    <span />
                    <span />
                    <span />
                  </div>
                  <span className="tiny mute">قوّةُ الكلمة: {strengthLabel}</span>
                </div>
              )}

              <button
                className="btn btn-primary mt-3"
                disabled={password.length < 4 || mismatch || save.isPending}
                onClick={() => save.mutate()}
              >
                <Icon name="check" size={15} />
                {save.isPending ? 'جارٍ الحفظ…' : isSet ? 'بدّل الكلمة' : 'اضبط الكلمة'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
